import { useCallback, useEffect, useRef, useState } from 'react'
import { useNav } from '../lib/nav'
import * as Sentry from '@sentry/react'
import { Page, TopBar } from '../components/Shell'
import { I } from '../components/Icons'
import { useScanner } from '../components/Scanner'
import { useToast } from '../components/Toast'
import { CoinNetworkRows, Seg } from '../components/Controls'
import { useStore } from '../state/store'
import { lookupPiece, searchPieces, loadTradeLimits, loadOwnerKeys } from '../lib/api'
import { belowFloor, fmtC, lineCents, ownerLabel, isConsigned, giveCents, tradeOut, tradeIn, tradeDirection, DIRECTION_LABEL, LABEL, type Piece, type SoldInfo, type TradeMethod, type TradeLimits, type Coin, type OwnerKeys } from '../lib/types'
import { SoldAlert } from './Sell'
import { Working } from '../components/StarTick'
import { useCountUp } from '../lib/motion'

export const CONSIGNED_MSG = (p: Piece) => `${p.title} is consigned${p.consignor?.name ? ` (${p.consignor.name})` : ''}. Consigned pieces can't go in a trade for now. Sell it on its own, or scan another piece.`

/** Right-hand settlement panel, shared by the trade screens. */
export function TradeAside({ cta, ctaLabel, ctaOff, limits, showMethod = true }: { cta: () => void; ctaLabel: string; ctaOff: boolean; limits: TradeLimits | null; showMethod?: boolean }) {
  const { trade, setTrade } = useStore()
  const out = tradeOut(trade), inn = tradeIn(trade), diff = out - inn
  const dir = tradeDirection(trade)
  // Motion: the three amounts roll to their new numbers; `out`, `inn` and `diff` themselves are what every decision reads
  const outShown = Math.round(useCountUp(out)), innShown = Math.round(useCountUp(inn)), diffShown = Math.round(useCountUp(Math.abs(diff)))
  const opts: { v: TradeMethod; label: string }[] = dir === 'starchive_pays'
    ? [{ v: 'cash', label: 'Cash' }, { v: 'zelle', label: 'Zelle' }, { v: 'wire', label: 'Wire' }, { v: 'crypto', label: 'Crypto' }]
    : [{ v: 'cash', label: 'Cash' }, { v: 'zelle', label: 'Zelle' }, { v: 'crypto', label: 'Crypto' }, { v: 'paypal', label: 'PayPal' }, { v: 'card', label: 'Card' }]
  const over = limits ? trade.give.filter((g) => giveCents(g) > limits.staff_credit_limit_cents).length : 0
  const below = trade.out.filter(belowFloor).length
  return (
    <>
      <div className="aside__body">
        <div className="icard-head"><b>Trade</b><span>{trade.out.length} out · {trade.give.length} in</span></div>
        <div className="card review-rows" style={{ margin: '0 20px' }}>
          <div className="row"><span className="row__label">They take</span><span className="row__value" style={{ fontWeight: 600 }}>{fmtC(outShown)}</span></div>
          <div className="row"><span className="row__label">They give (credit)</span><span className="row__value" style={{ fontWeight: 600 }}>{fmtC(innShown)}</span></div>
        </div>
        <div style={{ padding: '16px 20px 6px' }}>
          <div className="hint">{DIRECTION_LABEL[dir]}</div>
          <div style={{ fontSize: 34, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{dir === 'even' ? 'Even' : fmtC(diffShown)}</div>
        </div>
        {showMethod && dir !== 'even' && (
          <div style={{ padding: '0 20px' }}>
            <div className="hint" style={{ marginBottom: 6 }}>{dir === 'customer_pays' ? 'Customer pays by' : 'Starchive pays by'}</div>
            <Seg<TradeMethod> value={trade.method} options={opts} onChange={(method) => setTrade({ method })} />
            {trade.method === 'crypto' && <div style={{ marginTop: 8 }}><CoinNetworkRows compact coin={trade.coin} network={trade.network} onChange={(coin, network) => setTrade({ coin: coin as Coin, network })} /></div>}
            {trade.method === 'card' && <div className="hint" style={{ marginTop: 8 }}>In Shopify POS the trade-in credit of {fmtC(inn)} goes in as a Custom payment and the card takes {fmtC(Math.abs(diff))}.</div>}
          </div>
        )}
        {(over > 0 || below > 0) && <div className="banner" style={{ margin: '12px 20px 0' }}>{[below ? `${below} piece${below > 1 ? 's' : ''} below floor` : '', over ? `${over} credit${over > 1 ? 's' : ''} over the ${limits ? fmtC(limits.staff_credit_limit_cents) : ''} staff limit` : ''].filter(Boolean).join(' · ')}. An owner signs this trade.</div>}
        <div className="hint" style={{ padding: '14px 20px 0' }}>Each piece they give goes to the pending intake queue with its credit as its cost; Shane prices it when he lists it. Nothing leaves stock until the trade is paid.</div>
      </div>
      <div className="aside__foot">
        <button type="button" className="cta" disabled={ctaOff} onClick={cta}><span>{ctaLabel}</span><span>{dir === 'even' ? '' : fmtC(diffShown)}</span></button>
      </div>
    </>
  )
}

export function Trade() {
  const nav = useNav()
  const { trade, setTrade, resetTrade, customers } = useStore()
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState('')
  const toast = useToast()
  const [sold, setSold] = useState<{ piece: Piece; sold: SoldInfo } | null>(null)
  const [q, setQ] = useState('')
  const [results, setResults] = useState<(Piece & { sold_here: boolean; open_sale?: boolean })[] | null>(null)
  const [searching, setSearching] = useState(false)
  const [limits, setLimits] = useState<TradeLimits | null>(null)
  const [keys, setKeys] = useState<OwnerKeys>({})
  useEffect(() => { loadTradeLimits().then(setLimits).catch(() => {}); loadOwnerKeys().then(setKeys).catch(() => {}) }, [])
  const seq = useRef(0)
  useEffect(() => {
    const t = q.trim()
    if (t.length < 2) { setResults(null); return }
    const my = ++seq.current
    setSearching(true)
    const h = setTimeout(() => {
      searchPieces(t).then((r) => { if (my === seq.current) { setResults(r.pieces); setSearching(false) } }).catch(() => { if (my === seq.current) { setResults([]); setSearching(false) } })
    }, 350)
    return () => clearTimeout(h)
  }, [q])

  // Pieces come in at list price; tap one to change it. Consigned pieces are refused (decision 22 Sep).
  // Search results are thin (no Consignify owner or floor); adding one runs the same full lookup a scan does.
  const addPiece = async (p: Piece) => {
    if (trade.out.some((l) => l.piece.variant_id === p.variant_id)) { toast('That piece is already in this trade.'); return }
    setBusy(true); setMsg('')
    try {
      const r = await lookupPiece(p.barcode || p.sku)
      if (r.sold) { setSold({ piece: r.piece, sold: r.sold }); return }
      const full = r.piece.variant_id === p.variant_id ? r.piece : p
      if (isConsigned(full, keys)) { setMsg(CONSIGNED_MSG(full)); return }
      setTrade((t) => ({ out: [...t.out, { piece: full, price: String(Math.round(full.list_cents / 100)) }] }))
      setQ(''); setResults(null)
    } catch (e) { setMsg((e as Error).message) } finally { setBusy(false) }
  }
  const scan = useCallback(async (raw: string) => {
    const c = raw.trim(); if (!c) return
    setBusy(true); setMsg(''); setCode('')
    try {
      const r = await Sentry.startSpan({ name: 'counter.scan', op: 'counter.scan' }, () => lookupPiece(c))
      if (r.sold) { setSold({ piece: r.piece, sold: r.sold }); return }
      if (isConsigned(r.piece, keys)) { setMsg(CONSIGNED_MSG(r.piece)); return }
      if (trade.out.some((l) => l.piece.variant_id === r.piece.variant_id)) { toast('That piece is already in this trade.'); return }
      setTrade((t) => ({ out: [...t.out, { piece: r.piece, price: String(Math.round(r.piece.list_cents / 100)) }] }))
      if (r.open_sale) toast(`Heads up: this piece is also on unpaid sale #${r.open_sale.sale_no} (ID ${r.open_sale.by}). Cancel that one in Sales if it fell through.`, { ms: 5000 })
    } catch (e) {
      const m = (e as Error).message
      if (m.includes('No piece')) toast(`No piece with tag “${c}”. Try the SKU.`, { kind: 'bad' }); else setMsg(m)
      if (!m.includes('No piece')) Sentry.captureException(e)
    } finally { setBusy(false) }
  }, [setTrade, trade.out, keys, toast])
  useScanner(scan, !sold)

  const n = trade.out.length, g = trade.give.length
  const dir = tradeDirection(trade)
  // Motion: a piece scanned after the screen opened slides into the list; the ones already there sit still
  const seenOut = useRef(new Set(trade.out.map((l) => l.piece.variant_id)))
  useEffect(() => { trade.out.forEach((l) => seenOut.current.add(l.piece.variant_id)) })
  const cust = customers.find((c) => c.id === trade.customerId)
  const ready = n > 0 && g > 0 && trade.out.every((l) => lineCents(l) > 0) && (dir === 'even' || !!trade.method) && !!trade.customerId
  const ctaLabel = n === 0 ? 'Scan what they take' : g === 0 ? 'Add what they give' : !trade.customerId ? 'Pick the customer' : (dir !== 'even' && !trade.method) ? 'Choose how the difference is paid' : 'Review trade'
  return (
    <Page
      bar={<TopBar back="Home" backTo="/" title="New trade" caption={n || g ? `${n} out · ${g} in` : ''} />}
      aside={<TradeAside cta={() => nav('/trade/review')} ctaLabel={ctaLabel} ctaOff={!ready} limits={limits} />}
    >
      <div className="section-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><span>They take</span><span className="hint">at list price, tap to change</span></div>
      <div className="card">
        {trade.out.map((l, i) => (
          <button key={l.piece.variant_id} type="button" className={'person' + (seenOut.current.has(l.piece.variant_id) ? '' : ' cartline--in')} onClick={() => nav('/trade/price', { state: { step: i } })}>
            <span className="person__initial" style={{ borderRadius: 10 }}>{i + 1}</span>
            <span style={{ flexGrow: 1, minWidth: 0 }}><span className="person__name" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.piece.title}</span><span className="person__sub">{l.piece.sku || l.piece.barcode} · list {fmtC(l.piece.list_cents)} · {ownerLabel(l.piece.owner, l.piece.consignor_key, l.piece.consignor?.name)}</span></span>
            <span style={{ fontWeight: 700, color: belowFloor(l) ? '#d70015' : '#000', whiteSpace: 'nowrap' }}>{fmtC(lineCents(l))}{belowFloor(l) ? ' · below floor' : ''}</span>
            <button type="button" className="seg__btn" aria-label="Remove" onClick={(e) => { e.stopPropagation(); setTrade((t) => ({ out: t.out.filter((_, j) => j !== i) })) }}>×</button>
          </button>
        ))}
        <form className="scanpad__form" style={{ padding: '10px 16px' }} onSubmit={(e) => { e.preventDefault(); scan(code) }}>
          <input value={code} onChange={(e) => setCode(e.target.value)} placeholder={n ? 'Scan another tag, or type the SKU' : 'Scan the tag, or type the barcode or SKU'} inputMode="text" autoCapitalize="characters" autoCorrect="off" />
          <button type="submit" className="seg__btn" disabled={busy || !code.trim()}>{busy ? <Working>Looking</Working> : 'Add'}</button>
        </form>
        <div className="search searchbox" style={{ margin: '0 16px 12px' }}>{I.search()}<input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Or search by name, brand or SKU" autoCorrect="off" /></div>
        {results && (
          <div className="results" style={{ margin: '0 16px 12px' }}>
            {results.map((p) => (
              <button key={p.variant_id} type="button" className="person" disabled={p.sold_here || p.available <= 0} onClick={() => addPiece(p)} style={{ opacity: p.sold_here || p.available <= 0 ? 0.45 : 1 }}>
                <span className="thumb">{p.image_url ? <img src={p.image_url} alt="" /> : null}</span>
                <span style={{ flexGrow: 1, minWidth: 0, textAlign: 'left' }}><span className="person__name" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.title}</span><span className="person__sub">{p.sku || p.barcode} · {fmtC(p.list_cents)}{p.sold_here ? ' · sold' : p.available <= 0 ? ' · out of stock' : ''}</span></span>
                <span className="hint">{ownerLabel(p.owner, null)}</span>
              </button>
            ))}
            {results.length === 0 && !searching && <div className="empty">Nothing matches “{q}”.</div>}
            {searching && <div className="empty">Searching…</div>}
          </div>
        )}
      </div>
      {msg && <div className="banner" style={{ background: '#d70015' }}>{msg}</div>}

      <div className="section-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><span>They give</span><button type="button" className="seg__btn" onClick={() => nav('/trade/give')}>+ Add a piece</button></div>
      <div className="card">
        {trade.give.map((x, i) => (
          <button key={x.key} type="button" className="person" onClick={() => nav('/trade/give', { state: { index: i } })}>
            <span className="person__initial" style={{ borderRadius: 10, background: '#000', color: '#fff' }}>{I.trade(18)}</span>
            <span style={{ flexGrow: 1, minWidth: 0 }}><span className="person__name" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{x.brand} {x.item}{x.size ? ' Sz ' + x.size : ''}</span><span className="person__sub">{LABEL.cond[x.cond]} · {LABEL.whose[x.whose]}'s piece · pending intake</span></span>
            <span style={{ fontWeight: 700, whiteSpace: 'nowrap', color: limits && giveCents(x) > limits.staff_credit_limit_cents ? '#d70015' : '#000' }}>{fmtC(giveCents(x))}{limits && giveCents(x) > limits.staff_credit_limit_cents ? ' · over limit' : ''}</span>
            <button type="button" className="seg__btn" aria-label="Remove" onClick={(e) => { e.stopPropagation(); setTrade((t) => ({ give: t.give.filter((_, j) => j !== i) })) }}>×</button>
          </button>
        ))}
        {g === 0 && <div className="empty">Add each piece they're handing over, with the credit you're giving for it: what Starchive would pay to buy it for cash today.</div>}
      </div>

      <div className="section-title">Customer</div>
      <div className="card">
        <button type="button" className="row row--nav" onClick={() => nav('/customers?pick=trade')}><span className="row__label">Customer</span><span className="row__value">{cust ? `${cust.first_name} ${cust.last_name}`.trim() : 'Pick or add ›'}</span></button>
      </div>
      {(n > 0 || g > 0) && <button type="button" className="topbar__back" style={{ marginTop: 16, justifyContent: 'center' }} onClick={() => { resetTrade(); setMsg('') }}>Start over</button>}
      {sold && <SoldAlert piece={sold.piece} sold={sold.sold} onClose={() => setSold(null)} />}
    </Page>
  )
}
